import Cookies from 'js-cookie';
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import { debounce } from 'throttle-debounce';
import { ApolloClient, createHttpLink, InMemoryCache } from '@apollo/client';
import { datadogRum } from '@datadog/browser-rum';
import { GetJobSeekerProfileForPrefillDocument } from '../__generated__/graphql';
import { INITIAL_SYSTEM_STATE } from 'Configs/config';
import { getCurrentDate } from 'Utils/DateUtils';
import { timeoutLink } from 'Utils/ProfileGraphQLPolicy';
import { getFirstIndeedResume } from 'Utils/ProfileGraphQLUtils';
import CombinedStorage from 'Utils/storage/CombinedStorage';
import mergeResumeWithProfile from './mergeResumeWithProfile';
import { IS_LOGGED_IN } from './PassportUtil';
const LOCAL_STORAGE_KEY = 'rirekisho_multistep_values_v1';
const DEBOUNCE_DELAY_MS = 1000;
export default class LocalStorageManagerV1 {
    storeKey;
    static _instance;
    static instance() {
        return (this._instance ||
            (this._instance = new LocalStorageManagerV1(LOCAL_STORAGE_KEY, DEBOUNCE_DELAY_MS)));
    }
    system = INITIAL_SYSTEM_STATE;
    newResumeIds = {};
    initSystemPromise;
    storage;
    saveSystemHandler;
    debounceSaveSystemHandler;
    apolloClient;
    constructor(storeKey, debounceDelayMs) {
        this.storeKey = storeKey;
        this.storage = CombinedStorage.instance();
        this.saveSystemHandler = () => {
            this.storage.setItem(this.storeKey, this.system);
        };
        this.debounceSaveSystemHandler = debounce(debounceDelayMs, this.saveSystemHandler);
        this.initSystemPromise = this.initSystem();
        const prforceGroups = window.isPrivileged ? Cookies.get('prforceGroups') : undefined;
        this.apolloClient = new ApolloClient({
            cache: new InMemoryCache(),
            link: timeoutLink.concat(createHttpLink({
                uri: window.initialData.oneGraphApiUrl,
                credentials: 'include',
                headers: {
                    'Indeed-API-Key': window.initialData.oneGraphApiKey,
                    'indeed-ctk': Cookies.get('CTK') || '',
                    'indeed-co': 'JP',
                    ...(prforceGroups ? { 'indeed-prforce-groups': prforceGroups } : {})
                }
            }))
        });
    }
    clearValues() {
        this.storage.removeItem(this.storeKey);
    }
    async initSystem() {
        try {
            const values = await this.storage.getItem(this.storeKey);
            if (!values) {
                // saved value is empty
                this.system = INITIAL_SYSTEM_STATE;
                this.storage.setItem(this.storeKey, this.system);
            }
            else {
                this.system = values;
            }
        }
        catch (e) {
            datadogRum.addError(e);
            // Failed to decode values due to no value stored or decryption error.
            // Reset state and values to default
            this.system = INITIAL_SYSTEM_STATE;
            this.storage.setItem(this.storeKey, this.system);
        }
        // UNIV-1793: Fix critical resume problems
        this.fixSystems();
    }
    fixSystems() {
        // UNIV-1793: Fix critical resume problems
        Object.keys(this.system.resumes).forEach((key) => {
            if (Object.keys(this.system.resumes[key]).length < 3) {
                // The resume was mistakenly written to empty. We have to remove it.
                // If the user have logged in, the resume can recover from the server version,
                // or, it will be permanently lost.
                delete this.system.resumes[key];
            }
        });
    }
    async ready() {
        await this.initSystemPromise;
        return true;
    }
    getSystem() {
        return this.system;
    }
    saveSystem(v) {
        this.system = v;
        this.debounceSaveSystemHandler();
    }
    getRecentResume() {
        if (typeof this.system.recentId === 'undefined') {
            return undefined;
        }
        if (!(this.system.recentId in this.system.resumes)) {
            throw Error(`Recent resume ${this.system.recentId} not found!`);
        }
        return this.system.resumes[this.system.recentId];
    }
    setRecentResume(resumeId) {
        if (resumeId.trim() === '') {
            throw Error("resumeId can't be empty");
        }
        if (!(resumeId in this.system.resumes)) {
            throw Error(`resume '${resumeId}' is not found`);
        }
        this.system.recentId = resumeId;
        this.debounceSaveSystemHandler();
    }
    ensureResumeName(resume) {
        if (resume.resumeName) {
            return;
        }
        resume.resumeName = `${moment().format('YYYY-MM-DD')} ${resume.type === 'parttime' ? 'アルバイト・パート用' : '転職用'}`;
        // eslint-disable-next-line no-useless-escape
        const regexp = new RegExp(`^${resume.resumeName} \\(\(\\d+\)\\)$|^${resume.resumeName}$`);
        const usedNumbers = new Set();
        for (const resumeId of Object.keys(this.system.resumes)) {
            if (this.system.resumes[resumeId] === resume) {
                continue;
            }
            const match = this.system.resumes[resumeId].resumeName.match(regexp);
            if (!match) {
                continue;
            }
            usedNumbers.add(parseInt(match[1] || '0', 10));
        }
        let unusedSmallestNumber = 0;
        while (usedNumbers.has(unusedSmallestNumber))
            unusedSmallestNumber++;
        if (unusedSmallestNumber > 0) {
            resume.resumeName = `${resume.resumeName} (${unusedSmallestNumber})`;
        }
    }
    async getOrCreateRecentResume(id, initValues) {
        const resumeId = id;
        const { resumes } = this.system;
        if (typeof resumes[resumeId] === 'undefined' || Object.keys(resumes[resumeId]).length < 3) {
            resumes[resumeId] = cloneDeep(initValues);
            if (IS_LOGGED_IN) {
                const response = await this.apolloClient.query({
                    query: GetJobSeekerProfileForPrefillDocument
                });
                const firstIndeedResume = getFirstIndeedResume(response.data);
                resumes[resumeId] = mergeResumeWithProfile(resumes[resumeId], firstIndeedResume);
            }
            this.newResumeIds[resumeId] = true;
        }
        this.ensureResumeName(resumes[resumeId]);
        this.system.recentId = resumeId;
        this.debounceSaveSystemHandler();
        return this.getRecentResume();
    }
    saveRecentResume(v) {
        if (typeof this.system.recentId === 'undefined') {
            throw Error('recent resume is not found');
        }
        this.system.resumes[this.system.recentId] = {
            ...v,
            lastModifiedDate: getCurrentDate()
        };
        this.debounceSaveSystemHandler();
    }
    isRecentResumeNew() {
        const { recentId } = this.system;
        if (recentId) {
            return this.newResumeIds[recentId] || false;
        }
        else {
            return false;
        }
    }
    removeRecentResumeFromNew() {
        const { recentId } = this.system;
        if (recentId) {
            delete this.newResumeIds[recentId];
        }
    }
}
